import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORIES = [
  'General IT Support', 'Network & Connectivity', 'Hardware / Device',
  'Software / Application', 'Email & Accounts', 'Cybersecurity',
  'Cloud / Infrastructure', 'Data Recovery', 'Compliance / Audit', 'Other',
];

export default function CreateTicket() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium',
    service_type: 'remote', pricing_mode: 'fixed',
    price_amount: '', category: 'General IT Support',
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('general');
  const [industryFields, setIndustryFields] = useState({});
  const [kbSuggestions, setKbSuggestions] = useState([]);

  // Load industries
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/industries');
        setIndustries(data.industries || []);
      } catch (e) { /* handled */ }
    })();
  }, []);

  // Load package from URL params
  useEffect(() => {
    const packageId = searchParams.get('package');
    if (packageId) {
      (async () => {
        try {
          const { data } = await api.get(`/api/catalog/${packageId}`);
          setSelectedPackage(data);
          setForm(prev => ({
            ...prev,
            title: `${data.name} - Service Request`,
            description: `Service package: ${data.name}\n\n${data.description}\n\nIncludes:\n${(data.includes || []).map(i => `- ${i}`).join('\n')}`,
            category: data.category || prev.category,
            service_type: data.service_type || prev.service_type,
            pricing_mode: data.pricing_mode || prev.pricing_mode,
            price_amount: data.price || prev.price_amount,
          }));
        } catch (err) {
          console.error('Failed to load package:', err);
        }
      })();
    }
  }, [searchParams]);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selected].slice(0, 5));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  // Auto-suggest KB articles when title changes
  useEffect(() => {
    if (form.title.length < 5) { setKbSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/api/kb/suggest', { query: form.title });
        setKbSuggestions(data.suggestions || []);
      } catch (e) { /* handled */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form.title]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);

    try {
      const payload = {
        ...form,
        industry: selectedIndustry,
        industry_fields: industryFields,
        price_amount: form.price_amount ? parseFloat(form.price_amount) : null,
        package_id: selectedPackage?.id || null,
      };
      const { data: ticket } = await api.post('/api/tickets', payload);

      // Upload files if any
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('ticket_id', ticket.id);
        try {
          await api.post('/api/files/upload', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (err) {
          console.error('File upload failed:', err);
        }
      }

      toast.success('Ticket created successfully');
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="create-ticket-page">
      <Sidebar activeTab="Create Ticket" onTabChange={(tab) => {
        if (tab === 'Dashboard') navigate('/dashboard');
      }} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="max-w-2xl animate-fade-in">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>

          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 mb-1">New Support Request</h1>
          <p className="text-sm text-gray-500 mb-8">Describe your issue and we'll get the right help to you.</p>

          {selectedPackage && (
            <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="package-banner">
              <Package className="w-5 h-5 text-[#D4A843] mt-0.5 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#D4A843]">Service Package: {selectedPackage.name}</p>
                  <span className="font-heading text-lg font-bold text-[#D4A843]">
                    {selectedPackage.pricing_mode === 'quote' ? 'Custom Quote' : selectedPackage.pricing_mode === 'hourly' ? `$${selectedPackage.price}/hr` : `$${selectedPackage.price}`}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{selectedPackage.estimated_duration} | {selectedPackage.service_type} service</p>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedPackage(null); setForm({ title: '', description: '', priority: 'medium', service_type: 'remote', pricing_mode: 'fixed', price_amount: '', category: 'General IT Support' }); }}
                className="text-gray-400 hover:text-gray-600 shrink-0"
                data-testid="clear-package-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
              <div>
                <Label className="text-sm font-medium text-gray-700">Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  placeholder="Brief summary of the issue"
                  className="mt-1.5 rounded-lg"
                  required
                  data-testid="ticket-title-input"
                />
              </div>

              {/* KB Article Suggestions */}
              {kbSuggestions.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="kb-suggestions">
                  <p className="text-[10px] text-amber-700 font-bold uppercase tracking-wider mb-2">Related Knowledge Base Articles</p>
                  {kbSuggestions.slice(0, 3).map(a => (
                    <a key={a.id} href={`/kb`} className="block text-xs text-amber-700 hover:underline mb-1" data-testid={`kb-sug-${a.id}`}>{a.title}</a>
                  ))}
                </div>
              )}

              {/* Industry Selection */}
              <div>
                <Label className="text-sm font-medium text-gray-700">Industry Vertical</Label>
                <Select value={selectedIndustry} onValueChange={(v) => { setSelectedIndustry(v); setIndustryFields({}); }}>
                  <SelectTrigger className="mt-1.5 rounded-lg" data-testid="ticket-industry-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {industries.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {industries.find(i => i.id === selectedIndustry)?.requires_compliance && (
                  <p className="text-[10px] text-amber-600 mt-1 font-medium">This industry requires compliance-certified technicians</p>
                )}
              </div>

              {/* Dynamic Industry Fields */}
              {(industries.find(i => i.id === selectedIndustry)?.fields || []).length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-testid="industry-fields">
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{industries.find(i => i.id === selectedIndustry)?.name} Details</p>
                  {industries.find(i => i.id === selectedIndustry)?.fields.map(field => (
                    <div key={field.key}>
                      <Label className="text-xs">{field.label} {field.required && '*'}</Label>
                      {field.type === 'select' ? (
                        <Select value={industryFields[field.key] || ''} onValueChange={v => setIndustryFields(prev => ({...prev, [field.key]: v}))}>
                          <SelectTrigger className="mt-1 rounded-lg" data-testid={`field-${field.key}`}><SelectValue placeholder={`Select ${field.label}`} /></SelectTrigger>
                          <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : field.type === 'boolean' ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input type="checkbox" checked={industryFields[field.key] || false} onChange={e => setIndustryFields(prev => ({...prev, [field.key]: e.target.checked}))} className="rounded" data-testid={`field-${field.key}`} />
                          <span className="text-xs text-gray-600">Yes</span>
                        </div>
                      ) : (
                        <Input value={industryFields[field.key] || ''} onChange={e => setIndustryFields(prev => ({...prev, [field.key]: e.target.value}))} className="mt-1 rounded-lg" data-testid={`field-${field.key}`} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-gray-700">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder="Provide details about the issue, steps to reproduce, and any relevant context..."
                  className="mt-1.5 rounded-lg"
                  rows={5}
                  data-testid="ticket-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-1.5 rounded-lg" data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger className="mt-1.5 rounded-lg" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Service Type</Label>
                  <Select value={form.service_type} onValueChange={(v) => setForm({...form, service_type: v})}>
                    <SelectTrigger className="mt-1.5 rounded-lg" data-testid="ticket-service-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remote">Remote Support</SelectItem>
                      <SelectItem value="on_site">On-Site Support</SelectItem>
                      <SelectItem value="project">Project-Based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Pricing Mode</Label>
                  <Select value={form.pricing_mode} onValueChange={(v) => setForm({...form, pricing_mode: v})}>
                    <SelectTrigger className="mt-1.5 rounded-lg" data-testid="ticket-pricing-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Price</SelectItem>
                      <SelectItem value="hourly">Hourly Rate</SelectItem>
                      <SelectItem value="quote">Quote Request</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.pricing_mode !== 'quote' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    {form.pricing_mode === 'hourly' ? 'Budget ($/hr)' : 'Budget ($)'}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.price_amount}
                    onChange={(e) => setForm({...form, price_amount: e.target.value})}
                    placeholder="0.00"
                    className="mt-1.5 rounded-lg w-40"
                    data-testid="ticket-price-input"
                  />
                </div>
              )}
            </div>

            {/* File Upload */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">Attachments</Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-[#D4A843]/30 transition-colors">
                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-1">Drop files or click to upload</p>
                <p className="text-xs text-gray-400">Max 10MB per file. Up to 5 files.</p>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  style={{ position: 'relative' }}
                  data-testid="file-upload-input"
                />
              </div>
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((f, i) => (
                    <div key={`item-${i}`} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                      <span className="text-sm text-gray-700 truncate">{f.name}</span>
                      <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500" data-testid={`remove-file-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg px-8" data-testid="submit-ticket-btn">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} className="rounded-lg" data-testid="cancel-ticket-btn">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
